/**
 * lastWeek.js - the Dashboard's four "last week" boxes, as pure rules
 * (LastWeekBoxes.jsx draws them).
 *
 * "Last week" is the PREVIOUS FULL Monday-Sunday week in GLOBAL's time zone
 * (America/Detroit): every day from Monday Oct 5 to Sunday Oct 11 shows
 * Sep 28 - Oct 4, and on Monday Oct 12 it moves to Oct 5 - Oct 11. It is
 * absolute: the Dashboard's year filter does not apply.
 *
 * Days are 'YYYY-MM-DD' text throughout - the projects table stores its dates
 * as TEXT in that form, so a day is in the week when it sorts between the
 * two ends. Week arithmetic runs on UTC midnights of those days, so a
 * daylight-saving change in the week can never shift a boundary.
 *
 *   quoted   rows (any status) whose quote_date is in the week
 *   pos      rows whose po_date_issued is in the week AND that have a PO #
 *   invoices the invoices DATED in the week, from the invoice ledger
 *            (GET /api/finance/invoices, server/lib/invoiceLedger.js)
 *   collected the payments received in the week, from the same ledger
 *            (GET /api/finance/collected)
 */
import { isProjectRow } from '../../data/projectStatus';
import { opportunityHash, projectHash } from '../crm/nextStep';

export const DASH_TIME_ZONE = 'America/Detroit';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const utcOf = (day) => Date.parse(`${day}T00:00:00Z`);
const dayFromUtc = (t) => new Date(t).toISOString().slice(0, 10);

/** The calendar day it is at `now` in `timeZone`, as 'YYYY-MM-DD'. */
export function dayIn(now = new Date(), timeZone = DASH_TIME_ZONE) {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(
    now instanceof Date ? now : new Date(now)
  );
  const get = (type) => (parts.find((p) => p.type === type) || {}).value;
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** `day` moved by `n` days. */
export const addDays = (day, n) => dayFromUtc(utcOf(day) + n * 86400000);

/** { from, to }: the Monday-Sunday week before the one `today` is in. */
export function lastWeekRange(today) {
  const sinceMonday = (new Date(utcOf(today)).getUTCDay() + 6) % 7; // Monday 0 ... Sunday 6
  const thisMonday = addDays(today, -sinceMonday);
  return { from: addDays(thisMonday, -7), to: addDays(thisMonday, -1) };
}

const short = (day) => {
  const [, m, d] = day.split('-');
  return `${MONTHS[Number(m) - 1]} ${Number(d)}`;
};
/** 'Sep 28 - Oct 4' - the line on each box. */
export const rangeLabel = (r) => `${short(r.from)} - ${short(r.to)}`;
/** 'Monday Sep 28, 2026 - Sunday Oct 4, 2026' - the window's and the tooltip's. */
export const rangeLongLabel = (r) =>
  [r.from, r.to].map((d) => `${WEEKDAYS[new Date(utcOf(d)).getUTCDay()]} ${short(d)}, ${d.slice(0, 4)}`).join(' - ');

/** The stored day of a TEXT date ('2026-10-02', or a timestamp starting with one), else ''. */
export const dayOf = (v) => {
  const s = String(v == null ? '' : v).slice(0, 10);
  return DAY.test(s) ? s : '';
};
export const inRange = (v, r) => {
  const d = dayOf(v);
  return !!d && d >= r.from && d <= r.to;
};

/** A money cell -> number (0 for blank or text). Same guard as the Dashboard's
 *  pipeline: a figure of $500M or more is a typo, not a quote. */
export const amountOf = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(String(v == null ? '' : v).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) && Math.abs(n) < 500000000 ? n : 0;
};
const cents = (n) => Math.round(n * 100) / 100;

const pick = (p, camel, snake) => p[camel] ?? p[snake] ?? '';
const byQuote = (a, b) => String(b.quote).localeCompare(String(a.quote), undefined, { numeric: true });

/** A row as the windows show it. */
const rowOf = (p) => ({
  id: p.id,
  status: p.status || '',
  quote: pick(p, 'quoteLetter', 'quote_letter'),
  customer: p.customer || '',
  name: p.name || p.topic || p.description || '',
  owner: p.owner || '',
  quotedPrice: amountOf(pick(p, 'quotedPrice', 'quoted_price')),
  quoteDate: dayOf(pick(p, 'quoteDate', 'quote_date')),
  poNumber: pick(p, 'poNumber', 'po_number'),
  poValue: amountOf(pick(p, 'poValue', 'po_value')),
  poDate: dayOf(pick(p, 'poDateIssued', 'po_date_issued')),
  approvalManager: pick(p, 'approvalManager', 'approval_manager'),
});

/** Opportunities and projects quoted in the week: newest quote date first. */
export function quotedIn(projects, r) {
  const rows = (projects || [])
    .filter((p) => p && inRange(p.quoteDate ?? p.quote_date, r))
    .map(rowOf)
    .sort((a, b) => b.quoteDate.localeCompare(a.quoteDate) || byQuote(a, b));
  return { rows, count: rows.length, total: cents(rows.reduce((s, x) => s + x.quotedPrice, 0)) };
}

/**
 * POs issued in the week: newest PO date first. A record needs a PO number
 * to count - one marked Won by mistake and put back to Open keeps its PO date
 * but has no PO # (Sharif, 10/9/26).
 */
export function posIn(projects, r) {
  const rows = (projects || [])
    .filter((p) => p && inRange(p.poDateIssued ?? p.po_date_issued, r))
    .map(rowOf)
    .filter((x) => String(x.poNumber).trim() !== '')
    .sort((a, b) => b.poDate.localeCompare(a.poDate) || byQuote(a, b));
  return { rows, count: rows.length, total: cents(rows.reduce((s, x) => s + x.poValue, 0)) };
}

/**
 * The "by owner" subtotals of quoted rows: [{ owner, count, total }], the
 * biggest total first. `nameOf` turns a stored owner (often an email) into
 * the name shown; owners that read the same are one line. No owner is
 * '(no owner)', always last.
 */
export const NO_OWNER = '(no owner)';
export function byOwner(rows, nameOf = (o) => o) {
  const groups = new Map();
  for (const x of rows || []) {
    const owner = (x.owner && String(nameOf(x.owner) || x.owner).trim()) || NO_OWNER;
    const g = groups.get(owner) || { owner, count: 0, total: 0 };
    g.count += 1;
    g.total = cents(g.total + x.quotedPrice);
    groups.set(owner, g);
  }
  return [...groups.values()].sort(
    (a, b) => (a.owner === NO_OWNER) - (b.owner === NO_OWNER) || b.total - a.total || a.owner.localeCompare(b.owner)
  );
}

/** The $ collected box from GET /api/finance/collected: { count, total } of the week's payments. */
export function collectedSummary(data) {
  const payments = data && Array.isArray(data.payments) ? data.payments : [];
  return { count: payments.length, total: cents(payments.reduce((s, p) => s + (Number(p.amount) || 0), 0)) };
}

/**
 * The invoices box from the server's answer: `count` is the invoices (a row
 * with two numbers is two; one with no number is one - the ledger has one
 * number per row), `total` the dollars. Corrections (the old audit-log
 * reading's saves that LOWERED a total) are counted apart and never
 * subtracted; the ledger sends none.
 */
export function invoiceSummary(data) {
  const invoices = (data && data.invoices) || [];
  const corrections = (data && data.corrections) || [];
  return {
    count: invoices.reduce((s, i) => s + Math.max(1, (i.numbers || []).length), 0),
    total: cents(invoices.reduce((s, i) => s + (Number(i.amount) || 0), 0)),
    corrections: corrections.length,
    correctionTotal: cents(corrections.reduce((s, i) => s + (Number(i.amount) || 0), 0)),
  };
}

/** Where a row opens: a won job (Won / Complete) on Projects, anything else on Opportunities. */
export const recordHash = (row) => (isProjectRow(row) ? projectHash(row.id) : opportunityHash(row.id));

// what an invoice row shows: its total, its numbers, and its project's names
const INVOICE_FIELDS = ['invoiced_already', 'invoice_numbers', 'quote_letter', 'customer', 'name', 'po_number', 'status'];
/** Does a live change (src/live) touch what the invoices box shows? A Finance Invoice tab write always may. */
export const touchesInvoices = (evt) =>
  !!evt &&
  (evt.kind === 'finance-invoices' ||
    (evt.kind === 'projects' && !evt.sub && (evt.method === 'DELETE' || (evt.fields || []).some((f) => INVOICE_FIELDS.includes(f)))));
